import type { Edge, Node, SelectionChange } from '../types';

type Selectable = { id: string; selected?: boolean };

/** Node and edge `select` changes produced by one selection action. */
export type SelectionChanges = { nodeChanges: SelectionChange[]; edgeChanges: SelectionChange[] };

const select = (id: string, selected: boolean): SelectionChange => ({ id, type: 'select', selected });

/** Changes that make exactly `selectedIds` selected; items already in the right state get no change. */
export const getSelectionChanges = (items: readonly Selectable[], selectedIds: readonly string[]): SelectionChange[] =>
  items.flatMap((item) => {
    const shouldSelect = selectedIds.includes(item.id);
    return Boolean(item.selected) === shouldSelect ? [] : [select(item.id, shouldSelect)];
  });

/** v11: `node.selectable` wins; everything is selectable by default. */
export const isNodeSelectable = (node: Readonly<Node>): boolean => node.selectable ?? true;

/**
 * Clicking a node (or starting to drag it): an unselected node becomes the only selection (with the multi-select key
 * it is added); a selected node is deselected only with the multi-select key.
 */
export const getNodeClickSelection = (
  nodes: readonly Node[],
  edges: readonly Edge[],
  nodeId: string,
  isMultiSelect: boolean,
): SelectionChanges => {
  const node = nodes.find((candidate) => candidate.id === nodeId);
  if (!node) return { nodeChanges: [], edgeChanges: [] };
  if (node.selected) {
    return { nodeChanges: isMultiSelect ? [select(nodeId, false)] : [], edgeChanges: [] };
  }
  if (isMultiSelect) return { nodeChanges: [select(nodeId, true)], edgeChanges: [] };
  return { nodeChanges: getSelectionChanges(nodes, [nodeId]), edgeChanges: getSelectionChanges(edges, []) };
};

/** Clicking an edge: same rules as nodes, but a plain click also clears the node selection (v11). */
export const getEdgeClickSelection = (
  nodes: readonly Node[],
  edges: readonly Edge[],
  edgeId: string,
  isMultiSelect: boolean,
): SelectionChanges => {
  const edge = edges.find((candidate) => candidate.id === edgeId);
  if (!edge) return { nodeChanges: [], edgeChanges: [] };
  if (edge.selected && isMultiSelect) return { nodeChanges: [], edgeChanges: [select(edgeId, false)] };
  if (isMultiSelect) return { nodeChanges: [], edgeChanges: [select(edgeId, true)] };
  return { nodeChanges: getSelectionChanges(nodes, []), edgeChanges: getSelectionChanges(edges, [edgeId]) };
};

/** Clicking the pane deselects everything. */
export const getClearSelection = (nodes: readonly Node[], edges: readonly Edge[]): SelectionChanges => ({
  nodeChanges: getSelectionChanges(nodes, []),
  edgeChanges: getSelectionChanges(edges, []),
});

/** Selected nodes are lifted above the others (v11 `elevateNodesOnSelect`). */
export const SELECTED_NODE_Z_OFFSET = 1000;

export const getNodeZIndex = (node: Readonly<Node>): number =>
  (node.zIndex ?? 0) + (node.selected ? SELECTED_NODE_Z_OFFSET : 0);
